import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import {
  adminUpdateStudentPhase,
  getStudentFullDetail,
  getStudentOverview,
} from '../../lib/api';
import type { CoursePhase, OverviewCurriculum, OverviewStudent, StudentFullDetail } from '../../lib/api';
import { CurriculumRoadmap, curriculumProgress } from '../../components/CurriculumRoadmap';
import { DeactivateStudentModal } from '../../components/StudentModals';
import type { CurriculumProgress } from '../../components/CurriculumRoadmap';

// Student Overview — every active student's course progress at a glance, and
// everything about one student (curriculum day by day, attendance, tasks,
// fees, classes, contact) on the same page.

const PHASES: CoursePhase[] = ['First 5 Days', 'Mid Course', 'Last 10 Days'];

const PHASE_COLORS: Record<CoursePhase, { bg: string; color: string }> = {
  'First 5 Days': { bg: '#fef9c3', color: '#854d0e' },
  'Mid Course':   { bg: '#dbeafe', color: '#1d4ed8' },
  'Last 10 Days': { bg: '#dcfce7', color: '#15803d' },
};

type Stage = 'none' | 'notStarted' | 'early' | 'mid' | 'late' | 'complete';

const STAGES: Array<{ key: Stage; label: string }> = [
  { key: 'notStarted', label: 'Not started' },
  { key: 'early',      label: 'Just started (under 20%)' },
  { key: 'mid',        label: 'In progress (20–69%)' },
  { key: 'late',       label: 'Final stretch (70%+)' },
  { key: 'complete',   label: 'Course completed' },
  { key: 'none',       label: 'No curriculum' },
];

type SortKey = 'name' | 'progressAsc' | 'progressDesc' | 'attendanceAsc' | 'attention';

const SORTS: Array<{ key: SortKey; label: string }> = [
  { key: 'name',          label: 'Name (A–Z)' },
  { key: 'progressAsc',   label: 'Least progress first' },
  { key: 'progressDesc',  label: 'Most progress first' },
  { key: 'attendanceAsc', label: 'Lowest attendance first' },
  { key: 'attention',     label: 'Needs attention first' },
];

interface Row extends OverviewStudent {
  curriculum: OverviewCurriculum | null;
  progress: CurriculumProgress | null;
  pct: number | null;              // null = no curriculum
  stage: Stage;
  tasksPct: number | null;
  attention: string[];             // why this student needs a look
}

function toRow(s: OverviewStudent, curricula: Record<string, OverviewCurriculum>): Row {
  const curriculum = curricula[s.curriculumKey] || null;
  const progress = curriculum ? curriculumProgress(curriculum.days, s.completedDays) : null;
  const tasks = s.tasksDone + s.tasksMissed + s.tasksPending;
  const counted = s.taskCount && s.taskCount.given > 0 ? s.taskCount : null;

  const attention: string[] = [];
  if (s.atRisk) attention.push('Missed the last 3 classes');
  if (s.idle && s.daysIdle !== null) {
    attention.push(s.lastAttended ? `No class attended in ${s.daysIdle} days` : `Hasn't attended since joining (${s.daysIdle} days)`);
  }
  if (progress && progress.done === 0 && s.held >= 3) attention.push(`${s.held} classes attended, no days ticked off`);

  return {
    ...s,
    curriculum,
    progress,
    pct: progress ? progress.pct : null,
    stage: !progress ? 'none'
      : progress.done === 0 ? 'notStarted'
      : progress.done === progress.total ? 'complete'
      : progress.pct >= 70 ? 'late'
      : progress.pct >= 20 ? 'mid'
      : 'early',
    // by number of tasks, as the student sees it; by classes when no counts were typed
    tasksPct: counted ? Math.round((counted.done / counted.given) * 100)
      : tasks ? Math.round((s.tasksDone / tasks) * 100) : null,
    attention,
  };
}

// ── Small helpers ────────────────────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDay(day: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return day || '—';
  return `${Number(day.slice(8, 10))} ${MONTHS[Number(day.slice(5, 7)) - 1]} ${day.slice(0, 4)}`;
}

function ago(day: string, today: string): string {
  if (!day || !today) return '';
  const n = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${day}T00:00:00Z`)) / 86400000);
  return n <= 0 ? 'today' : n === 1 ? 'yesterday' : `${n} days ago`;
}

const initials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const attColor = (p: number | null) => p === null ? '#9ca3af' : p >= 75 ? '#16a34a' : p >= 50 ? '#d97706' : '#dc2626';
const progressColor = (r: Row) => r.stage === 'complete' ? '#16a34a' : '#6d28d9';
const rupees = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

const Bar: React.FC<{ pct: number; color: string; className?: string }> = ({ pct, color, className = '' }) => (
  <div className={`h-1.5 rounded-full bg-gray-100 overflow-hidden ${className}`}>
    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }} />
  </div>
);

const selectCls = 'text-xs border border-gray-200 rounded-lg px-2.5 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400';

// ── Page ─────────────────────────────────────────────────────────────────────

const AdminStudentOverview: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();

  const [data, setData]       = useState<{ today: string; curricula: Record<string, OverviewCurriculum>; students: OverviewStudent[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [search, setSearch]             = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [filterMentor, setFilterMentor] = useState('');
  const [filterPhase, setFilterPhase]   = useState('');
  const [filterStage, setFilterStage]   = useState<'' | Stage>('');
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [sort, setSort]                 = useState<SortKey>('name');

  const [updatingPhase, setUpdatingPhase]   = useState<string | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<Row | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getStudentOverview(adminPassword);
      setData({ today: res.today, curricula: res.curricula, students: res.students });
    } catch (err: any) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => (data ? data.students.map(s => toRow(s, data.curricula)) : []), [data]);

  const courses = useMemo(() => Array.from(new Set(rows.map(r => r.course).filter(Boolean))).sort(), [rows]);
  const mentors = useMemo(() => Array.from(new Set(rows.map(r => r.mentorName).filter(Boolean))).sort(), [rows]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter(r => {
      if (q && ![r.name, r.studentId, r.course, r.mentorName].some(v => (v || '').toLowerCase().includes(q))) return false;
      if (filterCourse && r.course !== filterCourse) return false;
      if (filterMentor && r.mentorName !== filterMentor) return false;
      if (filterPhase && r.phase !== filterPhase) return false;
      if (filterStage && r.stage !== filterStage) return false;
      if (attentionOnly && r.attention.length === 0) return false;
      return true;
    });
    const byName = (a: Row, b: Row) => a.name.localeCompare(b.name);
    // students with no curriculum / no classes go last whichever way it's sorted
    const pctUp   = (r: Row) => (r.pct === null ? 1e9 : r.pct);
    const pctDown = (r: Row) => (r.pct === null ? -1 : r.pct);
    const attUp   = (r: Row) => (r.attPct === null ? 1e9 : r.attPct);
    if (sort === 'progressAsc')   list.sort((a, b) => pctUp(a) - pctUp(b) || byName(a, b));
    if (sort === 'progressDesc')  list.sort((a, b) => pctDown(b) - pctDown(a) || byName(a, b));
    if (sort === 'attendanceAsc') list.sort((a, b) => attUp(a) - attUp(b) || byName(a, b));
    if (sort === 'attention')     list.sort((a, b) => b.attention.length - a.attention.length || (b.daysIdle ?? 0) - (a.daysIdle ?? 0) || byName(a, b));
    return list;
  }, [rows, search, filterCourse, filterMentor, filterPhase, filterStage, attentionOnly, sort]);

  // On a wide screen, open the first student so the page is never half empty
  useEffect(() => {
    if (!selectedId && visible.length > 0 && window.matchMedia('(min-width: 1024px)').matches) {
      setSelectedId(visible[0].studentId);
    }
  }, [selectedId, visible]);

  const selected = rows.find(r => r.studentId === selectedId) || null;

  const select = (id: string | null) => {
    setSelectedId(id);
    document.querySelector('main')?.scrollTo({ top: 0 });
  };

  // Summary
  const withCurriculum = rows.filter(r => r.pct !== null);
  const avgPct = withCurriculum.length
    ? Math.round(withCurriculum.reduce((s, r) => s + (r.pct || 0), 0) / withCurriculum.length) : null;
  const completedCount = rows.filter(r => r.stage === 'complete').length;
  const held    = rows.reduce((s, r) => s + r.held, 0);
  const classes = rows.reduce((s, r) => s + r.held + r.noShows, 0);
  const avgAtt  = classes ? Math.round((held / classes) * 100) : null;
  const attentionCount = rows.filter(r => r.attention.length > 0).length;

  const filtersOn = !!(search || filterCourse || filterMentor || filterPhase || filterStage || attentionOnly);
  const clearFilters = () => {
    setSearch(''); setFilterCourse(''); setFilterMentor(''); setFilterPhase(''); setFilterStage(''); setAttentionOnly(false);
  };

  const patchStudent = (studentId: string, patch: Partial<OverviewStudent>) =>
    setData(prev => prev && { ...prev, students: prev.students.map(s => (s.studentId === studentId ? { ...s, ...patch } : s)) });

  const handlePhaseChange = async (r: Row, phase: CoursePhase) => {
    if (updatingPhase) return;
    setUpdatingPhase(r.studentId);
    try {
      await adminUpdateStudentPhase(adminPassword, r.studentId, phase);
      patchStudent(r.studentId, { phase });
      toast.success(`${r.name} → ${phase}`);
    } catch (err: any) {
      toast.error('Failed to update phase: ' + err.message);
    } finally {
      setUpdatingPhase(null);
    }
  };

  // After the Deactivate dialog: they're no longer an active student
  const handleDeactivated = (studentId: string) => {
    setData(prev => prev && { ...prev, students: prev.students.filter(s => s.studentId !== studentId) });
    setSelectedId(null);
    setDeactivateTarget(null);
  };

  return (
    <TrackerLayout>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#2b2240' }}>Student Overview</h1>
            <p className="text-sm text-gray-500 mt-1">
              Course progress for every active student — pick one to see their full curriculum, classes, tasks and fees.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs font-semibold px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            {loading ? 'Loading…' : '↻ Refresh'}
          </button>
        </div>

        {/* Summary — on a phone, hidden while a student is open so they start at the top */}
        <div className={`${selected ? 'hidden lg:grid' : 'grid'} grid-cols-2 lg:grid-cols-4 gap-3 mb-4`}>
          <SummaryCard label="Active students" value={loading ? '…' : rows.length}
            sub={loading ? '' : `${withCurriculum.length} on a curriculum`} color="#2b2240" />
          <SummaryCard label="Avg course progress" value={avgPct !== null ? `${avgPct}%` : loading ? '…' : '—'}
            sub={loading ? '' : `${completedCount} finished the course`} color="#6d28d9" />
          <SummaryCard label="Attendance" value={avgAtt !== null ? `${avgAtt}%` : loading ? '…' : '—'}
            sub={loading ? '' : `${held} of ${classes} classes attended`} color={attColor(avgAtt)} />
          <SummaryCard label="Needs attention" value={loading ? '…' : attentionCount}
            sub="At risk, idle or no days ticked" color="#dc2626"
            active={attentionOnly} onClick={() => setAttentionOnly(v => !v)} />
        </div>

        {/* Filters */}
        <div className={`${selected ? 'hidden lg:flex' : 'flex'} bg-white rounded-2xl border border-gray-200 p-3 mb-4 flex-wrap items-center gap-2`}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name, ID, course or mentor…"
            className="flex-1 min-w-[200px] text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
          <select value={filterCourse} onChange={e => setFilterCourse(e.target.value)} className={selectCls}>
            <option value="">All courses</option>
            {courses.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterMentor} onChange={e => setFilterMentor(e.target.value)} className={selectCls}>
            <option value="">All mentors</option>
            {mentors.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={filterPhase} onChange={e => setFilterPhase(e.target.value)} className={selectCls}>
            <option value="">All phases</option>
            {PHASES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filterStage} onChange={e => setFilterStage(e.target.value as '' | Stage)} className={selectCls}>
            <option value="">All progress</option>
            {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select value={sort} onChange={e => setSort(e.target.value as SortKey)} className={selectCls} title="Sort">
            {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          {filtersOn && (
            <button onClick={clearFilters}
              className="text-xs font-semibold px-3 py-2 rounded-lg text-red-600 border border-red-200 bg-red-50 hover:bg-red-100">
              Clear
            </button>
          )}
        </div>

        {error && !data ? (
          <div className="bg-white rounded-2xl border border-red-200 p-8 text-center">
            <p className="text-sm font-semibold text-red-600">Couldn't load the overview</p>
            <p className="text-xs text-gray-500 mt-1">{error}</p>
            <button onClick={load} className="mt-4 text-xs font-semibold px-4 py-2 rounded-lg text-white" style={{ background: '#2b2240' }}>Try again</button>
          </div>
        ) : loading && !data ? (
          <div className="bg-white rounded-2xl border border-gray-200 py-20 text-center text-gray-400 text-sm">Loading students…</div>
        ) : (
          <div className="grid lg:grid-cols-[340px_minmax(0,1fr)] gap-4 items-start">

            {/* ── Student list ── */}
            <div className={`${selected ? 'hidden lg:flex' : 'flex'} flex-col bg-white rounded-2xl border border-gray-200 overflow-hidden lg:sticky lg:top-0 lg:max-h-[calc(100vh-7rem)]`}>
              <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500">{visible.length} of {rows.length} students</span>
                {attentionOnly && <span className="text-[11px] font-semibold text-red-600">Needs attention</span>}
              </div>
              <div className="flex-1 overflow-y-auto">
                {visible.length === 0 ? (
                  <div className="p-8 text-center text-sm text-gray-400">No students match these filters.</div>
                ) : visible.map(r => (
                  <StudentListItem key={r.studentId} row={r} selected={r.studentId === selectedId} onClick={() => select(r.studentId)} />
                ))}
              </div>
            </div>

            {/* ── Selected student ── */}
            <div className={selected ? '' : 'hidden lg:block'}>
              {selected && data ? (
                <StudentPanel
                  key={selected.studentId}
                  row={selected}
                  today={data.today}
                  adminPassword={adminPassword}
                  updatingPhase={updatingPhase === selected.studentId}
                  onPhaseChange={p => handlePhaseChange(selected, p)}
                  onDeactivate={() => setDeactivateTarget(selected)}
                  onBack={() => select(null)}
                />
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 py-24 text-center">
                  <p className="font-semibold text-gray-500">Select a student</p>
                  <p className="text-sm text-gray-400 mt-1">Their full curriculum, classes, tasks and fees show here.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {deactivateTarget && (
        <DeactivateStudentModal
          studentId={deactivateTarget.studentId}
          studentName={deactivateTarget.name}
          adminPassword={adminPassword}
          onClose={() => setDeactivateTarget(null)}
          onDone={() => handleDeactivated(deactivateTarget.studentId)}
        />
      )}
    </TrackerLayout>
  );
};

// ── Summary card ─────────────────────────────────────────────────────────────

const SummaryCard: React.FC<{
  label: string; value: React.ReactNode; sub?: string; color: string;
  active?: boolean; onClick?: () => void;
}> = ({ label, value, sub, color, active, onClick }) => {
  const body = (
    <>
      <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-black mt-1" style={{ color }}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5 leading-snug">{sub}</p>}
    </>
  );
  if (!onClick) return <div className="bg-white rounded-2xl border border-gray-200 p-4">{body}</div>;
  return (
    <button onClick={onClick} title={active ? 'Show everyone' : 'Show only these students'}
      className={`text-left bg-white rounded-2xl border p-4 transition-colors ${active ? 'border-red-300 ring-2 ring-red-100' : 'border-gray-200 hover:border-red-200'}`}>
      {body}
    </button>
  );
};

// ── List item ────────────────────────────────────────────────────────────────

const StudentListItem: React.FC<{ row: Row; selected: boolean; onClick: () => void }> = ({ row: r, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left px-4 py-3 border-b border-gray-100 transition-colors ${selected ? 'bg-[#f5f2fc]' : 'hover:bg-gray-50'}`}
    style={selected ? { boxShadow: 'inset 3px 0 0 #2b2240' } : undefined}
  >
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold shrink-0"
        style={selected ? { background: '#2b2240', color: '#F0B429' } : { background: '#f0edf7', color: '#2b2240' }}>
        {initials(r.name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-sm text-gray-900 truncate">{r.name}</span>
          {r.attention.length > 0 && (
            <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" title={r.attention.join(' · ')} />
          )}
          <span className="ml-auto text-xs font-bold shrink-0" style={{ color: r.pct === null ? '#9ca3af' : progressColor(r) }}>
            {r.pct === null ? '—' : `${r.pct}%`}
          </span>
        </div>
        <div className="text-[11px] text-gray-400 truncate mt-0.5">
          {r.studentId} · {r.mentorName || r.mentorId} · {r.phase}
        </div>
        {r.progress && <Bar pct={r.pct || 0} color={progressColor(r)} className="mt-2" />}
        <div className="text-[11px] text-gray-500 truncate mt-1.5">
          {!r.progress ? (
            <span className="text-gray-400">No curriculum · {r.course}</span>
          ) : r.progress.next ? (
            <>Next: <span className="font-semibold text-gray-700">D{r.progress.next.day}</span> · {r.progress.next.topic}</>
          ) : (
            <span className="font-semibold text-green-700">✓ Course complete</span>
          )}
        </div>
      </div>
    </div>
  </button>
);

// ── Everything about one student ─────────────────────────────────────────────

const TASK_STYLE: Record<string, string> = {
  'Completed':     'bg-green-50 text-green-700',
  'Task Given':    'bg-amber-50 text-amber-700',
  'Not Completed': 'bg-red-50 text-red-600',
};

const StudentPanel: React.FC<{
  row: Row;
  today: string;
  adminPassword: string;
  updatingPhase: boolean;
  onPhaseChange: (phase: CoursePhase) => void;
  onDeactivate: () => void;
  onBack: () => void;
}> = ({ row: r, today, adminPassword, updatingPhase, onPhaseChange, onDeactivate, onBack }) => {
  const [detail, setDetail]   = useState<StudentFullDetail | null>(null);
  const [detailError, setDetailError] = useState('');
  const [allClasses, setAllClasses]   = useState(false);

  useEffect(() => {
    let live = true;
    getStudentFullDetail(adminPassword, r.studentId)
      .then(res => { if (live) setDetail(res); })
      .catch(err => { if (live) setDetailError(err.message); });
    return () => { live = false; };
  }, [adminPassword, r.studentId]);

  const phaseStyle = PHASE_COLORS[r.phase] ?? { bg: '#f3f4f6', color: '#6b7280' };
  const classes  = r.held + r.noShows;
  const tasks    = r.tasksDone + r.tasksMissed + r.tasksPending;
  const pay      = detail?.payment || null;
  const balance  = pay ? Math.max(0, (pay.courseFee || 0) - (pay.totalPaid || 0)) : 0;
  const logs     = detail?.recentLogs || [];

  return (
    <div className="flex flex-col gap-4">

      {/* Who */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <button onClick={onBack} className="lg:hidden text-xs font-semibold text-gray-500 mb-3">← All students</button>
        <div className="flex flex-wrap items-start gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-black shrink-0"
            style={{ background: 'linear-gradient(135deg,#2b2240,#4e3a69)', color: '#F0B429' }}>
            {initials(r.name)}
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900">{r.name}</h2>
              <span className="text-[11px] font-mono font-bold px-2 py-0.5 rounded" style={{ background: '#2b2240', color: '#F0B429' }}>
                {r.studentId}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-0.5">{r.course}</p>
            <p className="text-xs text-gray-400 mt-1">
              Mentor <span className="font-semibold text-gray-700">{r.mentorName || r.mentorId}</span>
              {r.joined && <> · Joined {fmtDay(r.joined)}</>}
              {r.lastClass && <> · Last class {ago(r.lastClass.day, today)}{!r.lastClass.present && ' (absent)'}</>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {updatingPhase ? (
              <span className="text-xs text-gray-400 animate-pulse">Updating…</span>
            ) : (
              <select
                value={r.phase}
                onChange={e => onPhaseChange(e.target.value as CoursePhase)}
                title="Course phase"
                className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-yellow-400"
                style={{ backgroundColor: phaseStyle.bg, color: phaseStyle.color }}
              >
                {PHASES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
            <button
              onClick={onDeactivate}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50"
            >
              Deactivate
            </button>
          </div>
        </div>
        {r.attention.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {r.attention.map(a => (
              <span key={a} className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-red-50 text-red-700 border border-red-100">⚠ {a}</span>
            ))}
          </div>
        )}
      </div>

      {/* Numbers */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Kpi label="Course progress"
          value={r.progress ? `${r.progress.pct}%` : '—'}
          sub={r.progress ? `${r.progress.done} of ${r.progress.total} days done` : 'No curriculum'}
          color={r.pct === null ? '#9ca3af' : progressColor(r)} pct={r.pct} />
        <Kpi label="Attendance"
          value={r.attPct !== null ? `${r.attPct}%` : '—'}
          sub={classes ? `${r.held} of ${classes} classes attended` : 'No classes yet'}
          color={attColor(r.attPct)} pct={r.attPct} />
        <Kpi label="Tasks done"
          value={r.tasksPct !== null ? `${r.tasksPct}%` : '—'}
          sub={r.taskCount && r.taskCount.given > 0
            ? `${r.taskCount.done} of ${r.taskCount.given} tasks${r.tasksPending ? ` · ${r.tasksPending} class${r.tasksPending === 1 ? '' : 'es'} pending` : ''}`
            : tasks ? `${r.tasksDone} done · ${r.tasksMissed} missed · ${r.tasksPending} pending` : 'No tasks given yet'}
          color="#0f766e" pct={r.tasksPct} />
        <Kpi label="Fees paid"
          value={!detail && !detailError ? '…' : pay ? rupees(pay.totalPaid) : '—'}
          sub={!detail && !detailError ? 'Loading…' : pay ? (pay.courseFee ? `of ${rupees(pay.courseFee)} · ${rupees(balance)} due` : 'Course fee not set') : 'No payment record'}
          color="#b45309" pct={pay && pay.courseFee ? Math.round((pay.totalPaid / pay.courseFee) * 100) : null} />
      </div>

      {/* Curriculum */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <CurriculumRoadmap
          tone="light"
          title={r.curriculum?.title}
          days={r.curriculum?.days || []}
          completedDays={r.completedDays}
          checkpoints={r.curriculum?.projectCheckpoints}
          projectStatuses={r.projectStatuses}
        />
        {r.portionsUpdated && (
          <p className="text-[11px] text-gray-400 mt-3">Days last ticked {fmtDay(r.portionsUpdated)} ({ago(r.portionsUpdated, today)})</p>
        )}
      </div>

      {/* Classes + contact */}
      <div className="grid 2xl:grid-cols-5 gap-4 items-start">
        <div className="2xl:col-span-3 bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-gray-900">Recent classes</h3>
            {logs.length > 8 && (
              <button onClick={() => setAllClasses(v => !v)} className="text-xs font-semibold" style={{ color: '#6d28d9' }}>
                {allClasses ? 'Show fewer' : `Show last ${logs.length}`}
              </button>
            )}
          </div>
          {detailError ? (
            <p className="text-xs text-red-500">{detailError}</p>
          ) : !detail ? (
            <div className="space-y-2">{[0, 1, 2].map(i => <div key={i} className="h-9 rounded-lg bg-gray-50 animate-pulse" />)}</div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">No classes logged yet.</p>
          ) : (
            <div>
              {logs.slice(0, allClasses ? logs.length : 8).map((l, i) => (
                <div key={i} className="flex items-center gap-3 py-2.5 border-t border-gray-100 first:border-t-0">
                  <div className="w-20 shrink-0">
                    <div className="text-xs font-semibold text-gray-700">{l.date}</div>
                    <div className="text-[11px] text-gray-400">{l.day}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-800 truncate" title={l.topic}>{l.topic || '—'}</div>
                    {l.takenBy && <div className="text-[11px] text-gray-400 truncate">by {l.takenBy}</div>}
                  </div>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md shrink-0 ${l.attendance === 'Present' ? 'bg-green-50 text-green-700' : l.attendance === 'Absent' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                    {l.attendance}
                  </span>
                  {l.completionStatus && l.completionStatus !== 'N/A' && (
                    <span className={`hidden sm:inline text-[11px] font-semibold px-2 py-0.5 rounded-md shrink-0 ${TASK_STYLE[l.completionStatus] || 'bg-gray-100 text-gray-500'}`}>
                      {l.completionStatus}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="2xl:col-span-2 bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Contact &amp; account</h3>
          {detailError ? (
            <p className="text-xs text-red-500">{detailError}</p>
          ) : !detail ? (
            <div className="space-y-2">{[0, 1, 2, 3].map(i => <div key={i} className="h-5 rounded bg-gray-50 animate-pulse" />)}</div>
          ) : (
            <>
              <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm">
                {([
                  ['Email',        detail.student.email],
                  ['Phone',        detail.student.phone],
                  ['Sales person', detail.student.salesPerson],
                  ['Session',      pay?.sessionType],
                  ['Enrolled',     pay?.enrollDate || detail.student.dateJoined],
                ] as const).map(([label, value]) => (
                  <React.Fragment key={label}>
                    <dt className="text-xs text-gray-400 pt-0.5">{label}</dt>
                    <dd className="text-gray-800 break-words">{value || '—'}</dd>
                  </React.Fragment>
                ))}
                <dt className="text-xs text-gray-400 pt-0.5">Portal login</dt>
                <dd>
                  {!detail.auth ? (
                    <span className="text-xs font-semibold text-gray-500">No account yet</span>
                  ) : detail.auth.isFirstLogin ? (
                    <span className="text-xs font-semibold text-amber-700">Never logged in</span>
                  ) : (
                    <span className="text-xs font-semibold text-green-700">✓ Has logged in</span>
                  )}
                </dd>
              </dl>
              {detail.student.notes && (
                <div className="mt-4 p-3 rounded-xl text-xs text-gray-700" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                  <div className="font-bold text-amber-800 mb-1">Notes</div>
                  {detail.student.notes}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Kpi: React.FC<{ label: string; value: React.ReactNode; sub: string; color: string; pct: number | null }> =
  ({ label, value, sub, color, pct }) => (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 min-w-0">
      <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-black mt-1" style={{ color }}>{value}</p>
      <p className="text-xs text-gray-500 mt-0.5 leading-snug">{sub}</p>
      {pct !== null && <Bar pct={pct} color={color} className="mt-2.5" />}
    </div>
  );

export default AdminStudentOverview;
